import { TrendingUp, TrendingDown, Minus } from "lucide-react";

const fmt = (n, d = 1) =>
  n === null || n === undefined ? "—" : Number(n).toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d });

function timeAgo(iso) {
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 3600) return `${Math.max(1, Math.floor(s / 60))} Minutes Ago`;
  if (s < 86400) return `${Math.floor(s / 3600)} Hours Ago`;
  return `${Math.floor(s / 86400)} Days Ago`;
}

function Chart({ card }) {
  const { spark, decimals: d, direction, confidence } = card;
  const closes = spark?.closes || [];
  if (closes.length < 10) return null;
  const W = 720, HISTX = 500, PROJX = 640, H = 260, RSITOP = 285, RSIH = 70, TOTALH = 375;
  const all = [...closes, card.target, card.stop, card.key_level].filter((v) => v != null);
  const lo = Math.min(...all), hi = Math.max(...all);
  const pad = (hi - lo) * 0.08 || 1;
  const y = (p) => 14 + (H - 28) * (1 - (p - (lo - pad)) / (hi + pad - (lo - pad)));
  const x = (i) => 8 + (HISTX - 16) * (i / (closes.length - 1));
  const lastX = x(closes.length - 1), lastY = y(closes[closes.length - 1]);
  const tY = y(card.target);
  const spread = direction === "neutral" ? 0 : (tY - lastY) * 0.28;
  const fans = [
    { yEnd: tY, label: `${Math.min(confidence + 16, 97)}%` },
    { yEnd: tY - spread, label: `${Math.min(confidence + 8, 95)}%` },
    { yEnd: tY - spread * 2, label: `${confidence}%` },
  ];
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((f) => lo - pad + (hi + pad - (lo - pad)) * f);
  const ts = spark.ts || [];
  const fmtDate = (t) => {
    const dt = new Date(t * 1000);
    return `${dt.getMonth() + 1}/${dt.getDate()} ${String(dt.getHours()).padStart(2, "0")}:00`;
  };
  const rsiVals = (spark.rsi || []).map((r, i) => [i, r]).filter(([, r]) => r != null);
  const ry = (r) => RSITOP + 6 + (RSIH - 12) * (1 - r / 100);
  return (
    <svg viewBox={`0 0 ${W} ${TOTALH}`} className="w-full" data-testid={`analysis-chart-${card.symbol_id}`}>
      <text x={HISTX / 2 - 60} y={H / 2 - 8} fill="rgba(255,255,255,0.06)" fontSize="42" fontWeight="600" fontFamily="inherit">MBM</text>
      <text x={HISTX / 2 - 58} y={H / 2 + 14} fill="rgba(255,255,255,0.05)" fontSize="11" letterSpacing="3">MIND BEFORE MARKETS</text>
      {closes.slice(1).map((c, i) => (
        <line key={i} x1={x(i)} y1={y(closes[i])} x2={x(i + 1)} y2={y(c)}
          stroke={c >= closes[i] ? "#7ee0a1" : "#ff7a7a"} strokeWidth="1.6" />
      ))}
      {/* key level */}
      <line x1={8} y1={y(card.key_level)} x2={PROJX + 40} y2={y(card.key_level)} stroke="#8fd3b8" strokeWidth="1" opacity="0.8" />
      <rect x={16} y={y(card.key_level) - 11} width={86} height={20} rx={3} fill="#1c2622" />
      <text x={59} y={y(card.key_level) + 3} fill="#cfe9dd" fontSize="12" textAnchor="middle">{fmt(card.key_level, d)}</text>
      {/* breakout marker */}
      <line x1={lastX} y1={10} x2={lastX} y2={H - 6} stroke="#9ca3af" strokeWidth="1" strokeDasharray="4 4" opacity="0.6" />
      {/* projection fan */}
      {direction !== "neutral" && fans.map((f, i) => (
        <g key={i}>
          <line x1={lastX} y1={lastY} x2={PROJX} y2={f.yEnd} stroke="#8fd3b8" strokeWidth="1" opacity="0.9" />
          <text x={PROJX - 34} y={f.yEnd + (f.yEnd > lastY ? 14 : -6)} fill="#bfe6d4" fontSize="11">{f.label}</text>
        </g>
      ))}
      {/* target box */}
      <rect x={PROJX - 10} y={tY - 12} width={92} height={22} rx={3} fill="#e6c67a" />
      <text x={PROJX + 36} y={tY + 3} fill="#0A0A0A" fontSize="12" fontWeight="600" textAnchor="middle">{fmt(card.target, d)}</text>
      {/* y ticks */}
      {ticks.map((t, i) => (
        <text key={i} x={W - 4} y={y(t) + 4} fill="#71717a" fontSize="11" textAnchor="end">{fmt(t, d)}</text>
      ))}
      {/* x labels */}
      {ts.length > 2 && (
        <>
          <text x={12} y={H + 12} fill="#71717a" fontSize="11">{fmtDate(ts[0])}</text>
          <text x={HISTX / 2} y={H + 12} fill="#71717a" fontSize="11" textAnchor="middle">{fmtDate(ts[Math.floor(ts.length / 2)])}</text>
          <text x={lastX} y={H + 12} fill="#d4d4d8" fontSize="11" fontWeight="600" textAnchor="middle">{fmtDate(ts[ts.length - 1])}</text>
        </>
      )}
      {/* RSI */}
      <text x={10} y={RSITOP + 2} fill="#a1a1aa" fontSize="11">RSI</text>
      <line x1={8} y1={ry(70)} x2={PROJX} y2={ry(70)} stroke="#3f3f46" strokeWidth="0.7" />
      <line x1={8} y1={ry(30)} x2={PROJX} y2={ry(30)} stroke="#3f3f46" strokeWidth="0.7" />
      <text x={PROJX + 14} y={ry(70) + 4} fill="#71717a" fontSize="11">70</text>
      <text x={PROJX + 14} y={ry(30) + 4} fill="#71717a" fontSize="11">30</text>
      <polyline points={rsiVals.map(([i, r]) => `${x(i)},${ry(r)}`).join(" ")} fill="none" stroke="#8fd3b8" strokeWidth="1.2" />
    </svg>
  );
}

export const AnalysisCard = ({ card, badge = "MBM INTELLIGENCE" }) => {
  const d = card.decimals ?? 1;
  const dir = card.direction;
  const DirIcon = dir === "bullish" ? TrendingUp : dir === "bearish" ? TrendingDown : Minus;
  const dirColor = dir === "bullish" ? "#7ee0a1" : dir === "bearish" ? "#ff7a7a" : "#a1a1aa";
  const expiry = new Date(card.expires_at);
  return (
    <div className="rounded-2xl border border-white/10 bg-[#050505] overflow-hidden" data-testid={`analysis-card-${card.symbol_id}`}>
      <div className="flex items-center justify-between px-5 sm:px-6 py-4">
        <div className="flex items-center gap-3">
          <span className="h-9 w-9 grid place-items-center rounded bg-white/8 font-heading text-sm font-medium">M</span>
          <div>
            <div className="section-label text-[8px] text-zinc-500">Mind Before Markets</div>
            <div className="text-[13px] text-zinc-100 font-semibold tracking-wide">AI MARKET ANALYSIS</div>
          </div>
        </div>
        <span className="section-label text-[9px] px-3.5 py-2 rounded-full border border-[#8fd3b8]/40 text-[#8fd3b8]">{badge}</span>
      </div>
      <div className="border-t border-white/8 px-5 sm:px-6 py-5">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-2.5">
            <h3 className="font-heading text-2xl sm:text-3xl font-medium tracking-tight">{card.name}</h3>
            <DirIcon className="h-5 w-5" style={{ color: dirColor }} />
          </div>
          <span className="text-[12px] text-zinc-400 whitespace-nowrap mt-1.5">{card.timeframe} Timeframe</span>
        </div>
        <div className="mt-1.5 text-sm text-zinc-200 font-medium">
          {card.pattern} <span className="text-zinc-600 font-light"> · {timeAgo(card.generated_at)}</span>
        </div>
        <div className="mt-4 grid grid-cols-2 gap-x-6 gap-y-3">
          <div><div className="section-label text-[9px] text-zinc-500">Entry Level</div><div className="mt-0.5 font-heading text-lg" data-testid="card-entry">{fmt(card.entry, d)}</div></div>
          <div><div className="section-label text-[9px] text-zinc-500">Target Level</div><div className="mt-0.5 font-heading text-lg text-[#7ee0a1]" data-testid="card-target">{fmt(card.target, d)}</div></div>
          <div><div className="section-label text-[9px] text-zinc-500">Stop-Loss</div><div className="mt-0.5 font-heading text-lg text-[#ff7a7a]" data-testid="card-stop">{fmt(card.stop, d)}</div></div>
          <div><div className="section-label text-[9px] text-zinc-500">Target Period</div><div className="mt-0.5 font-heading text-lg">{card.period_days} Day{card.period_days === 1 ? "" : "s"}</div></div>
        </div>
        <div className="mt-4 rounded-xl bg-white/[0.04] border border-white/6 p-4">
          <p className="text-[13px] text-zinc-200 font-light leading-relaxed">{card.narrative}</p>
          <p className="mt-2.5 pt-2.5 border-t border-white/6 text-[11px] text-zinc-500 font-light">
            Illustrative example for teaching pattern recognition — not a live trade call.
          </p>
        </div>
        <div className="mt-4"><Chart card={card} /></div>
        <div className="mt-3 pt-3 border-t border-white/8">
          <div className="text-[12px] text-zinc-300">Expiry Date/Time: {expiry.getMonth() + 1}/{expiry.getDate()} {String(expiry.getHours()).padStart(2, "0")}:{String(expiry.getMinutes()).padStart(2, "0")}</div>
          <div className="mt-1 text-[11px] text-zinc-500 font-light">Educational analysis only. Not financial advice. MBM does not hold an AFSL.</div>
          <div className="mt-0.5 text-[11px] text-[#8fd3b8]">mindbeforemarkets.com</div>
          {card.kind === "setup" && card.author && (
            <div className="mt-1 text-[11px] text-zinc-500">Published by {card.author}</div>
          )}
        </div>
      </div>
    </div>
  );
};
